import { inject, Injectable, type OnDestroy, signal } from '@angular/core';
import { type Subscription, take } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ModalCompareService } from '../modal-compare.service';
import { type ModalInstance } from '../modal.types';

@Injectable()
export class ModalWindowCompareController implements OnDestroy {
  private modalCompare = inject(ModalCompareService);

  // show compare button if 2 modals are open
  canCompare = signal(false);
  isRight = signal(false);
  isLeft = signal(false);
  isComparing = signal(false);
  // used to place the minimized windows next to each other
  modalIndex: number;

  private modalSubscriptionSink: Subscription;
  private modalInstance: ModalInstance;

  init(modalInstance: ModalInstance) {
    this.modalInstance = modalInstance;

    this.listenForComparingEvents();
  }

  ngOnDestroy() {
    this.modalSubscriptionSink?.unsubscribe();
  }

  setComparingMode(compare: boolean, modals: ModalInstance[]) {
    this.modalCompare.setComparingMode(compare);
    this.modalInstance.comparing = compare;
    this.modalCompare.notifyModals(modals);
  }

  isComparingMode() {
    return this.modalCompare.comparingMode;
  }

  startCompare() {
    // modal was minimized, but other modal requested comparing mode
    this.modalInstance.comparing = true;
    // resubscribe on the modals once, to update the state of all modals
    this.modalCompare.modals$.pipe(take(1)).subscribe((modals) => this.showCompare(modals));
  }

  listenForComparingEvents() {
    this.modalSubscriptionSink = this.modalCompare.modals$
      .pipe(tap((modals) => this.determineModalIndex(modals)))
      .subscribe((modals) => this.showCompare(modals));

    this.modalSubscriptionSink.add(
      this.modalCompare.comparing$.subscribe((isComparing) => this.onComparing(isComparing)),
    );
  }

  // handler from notifyComparing().
  private onComparing(isComparing: boolean) {
    // only set the window to comparing when the modalInstance is set to compare mode.
    this.isComparing.set(!!(this.modalInstance.comparing && isComparing));

    if (this.isComparing()) {
      // get the latest state of the modals, to show one left and one right.
      this.modalCompare.modals$.pipe(take(1)).subscribe((modals) => {
        const comparingModals = modals.filter((modal) => modal.comparing);
        this.isLeft.set(comparingModals[1] === this.modalInstance);
        this.isRight.set(comparingModals[0] === this.modalInstance);
      });
    }
  }

  private determineModalIndex(modals: ModalInstance[]) {
    // used to determine minimized modal place
    this.modalIndex = modals.indexOf(this.modalInstance);
  }

  private showCompare(modals: ModalInstance[]) {
    // only compare if there are modals, and the global comparing mode is set
    // the global compare mode is set only when the compare button has been clicked.
    if (modals && this.modalCompare.comparingMode) {
      if (!modals.length) {
        // all modals closed, leaving comparing mode
        this.modalCompare.setComparingMode(false);
      }

      // all modals need to support comparing, so we can show confirmation modals over existing modals
      const onlyComparableModals = modals.every((it) => it.config.allowCompare);

      // get all the modals that want to compare
      const comparingModals = modals.filter((modal) => modal.comparing);

      // when a modal is opened from minimized state, and the previous one has requested comparing mode
      // the modal from minimized state is already set to compare mode.
      const comparingFromMinimized = comparingModals.length === 2;

      // when the next modal comes from the table instead of from minimized, it is not yet set to compare mode.
      // so it will not show up in the comparingModals, but is the next modal in the modals array.
      const modalFromTable = comparingModals.length === 1 && modals[modals.indexOf(comparingModals[0]) + 1];
      const comparingFromTable = !!modalFromTable;
      if (comparingFromTable) {
        modalFromTable.comparing = true;
        comparingModals.push(modalFromTable);
      }

      this.canCompare.set(onlyComparableModals && (comparingFromMinimized || comparingFromTable));

      // notify modals to go into compare mode
      this.modalCompare.notifyComparing(this.canCompare());
    } else {
      // every other action than compare, means we want to stop comparing
      this.modalCompare.setComparingMode(false);
    }
  }
}
